import React, { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Ruler, SquareStack, Frame, Layout, Upload, Trash2, Move, ImageIcon } from "lucide-react";
import { api, API_BASE, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdvancedLetterheadCard — Phases B/D/C/E/F letterhead controls.
 *   B. Page margins (top/right/bottom/left in mm) — applies to print + A4 preview
 *   D. Element gaps — section gap (mm between report sections), slot gap (px between stacked chips)
 *   C. Border style + watermark image
 *   E. Header/footer vertical alignment + one-click layout presets
 *   F. 4-zone border placements + header/footer inner padding + logo offset
 */
export default function AdvancedLetterheadCard({ form, setForm, onSave }) {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const wmInputRef = useRef(null);
  const DEFAULTS = {
    page_margin_top_mm: 0, page_margin_right_mm: 8, page_margin_bottom_mm: 0, page_margin_left_mm: 8,
    section_gap_mm: 5, slot_gap_px: 4,
    border_style: "solid",
    border_above_header_thickness: 0, border_below_header_thickness: 0,
    border_above_footer_thickness: 0, border_below_footer_thickness: 1,
    header_padding_top_mm: 2, header_padding_bottom_mm: 2,
    footer_padding_top_mm: 2, footer_padding_bottom_mm: 2,
    logo_offset_x_mm: 0, logo_offset_y_mm: 0,
    watermark_url: "", watermark_opacity: 0.06,
    header_align: "start", footer_align: "end",
  };
  const lh = { ...DEFAULTS, ...(form?.letterhead || {}) };
  const set = (k, v) => setForm({ ...form, letterhead: { ...(form.letterhead || {}), [k]: v } });
  const setMany = (patch) => setForm({ ...form, letterhead: { ...(form.letterhead || {}), ...patch } });

  const save = async () => {
    setSaving(true);
    try { await onSave(form.letterhead || lh); }
    finally { setSaving(false); }
  };

  const uploadWatermark = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Watermark must be under 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE}/files/${r.data.path}`;
      set("watermark_url", url);
      toast.success("Watermark uploaded — save to apply");
    } catch (err) { toast.error(apiError(err, "Upload failed")); }
    finally { setUploading(false); if (wmInputRef.current) wmInputRef.current.value = ""; }
  };

  // Phase E — one-click layout presets that repopulate slots + alignment.
  const applyPreset = (id) => {
    let patch = {};
    switch (id) {
      case "classic": // logo left, name+addr centered, contact right
        patch = {
          header_left: ["logo"], header_center: ["name_address"], header_right: ["contact", "accreditation"],
          footer_left: ["address"], footer_center: ["timestamp", "report_id"], footer_right: ["signatures"],
          header_align: "center", footer_align: "end",
        };
        break;
      case "logo-left-stacked": // everything in header_left column, footer split
        patch = {
          header_left: ["logo", "name", "tagline"], header_center: ["none"], header_right: ["contact", "qr"],
          footer_left: ["address", "license"], footer_center: ["social"], footer_right: ["signatures"],
          header_align: "start", footer_align: "end",
        };
        break;
      case "centered-hero": // fully centered branding
        patch = {
          header_left: ["none"], header_center: ["logo", "name", "tagline"], header_right: ["none"],
          footer_left: ["contact"], footer_center: ["disclaimer"], footer_right: ["signatures"],
          header_align: "center", footer_align: "center",
        };
        break;
      case "split-modern": // logo-left + contact/qr right (no center)
        patch = {
          header_left: ["logo", "name"], header_center: ["none"], header_right: ["contact", "working_hours"],
          footer_left: ["registration", "gst"], footer_center: ["custom_logos"], footer_right: ["signatures", "timestamp"],
          header_align: "start", footer_align: "end",
        };
        break;
      case "minimal": // logo only header, single-line footer
        patch = {
          header_left: ["logo"], header_center: ["none"], header_right: ["name"],
          footer_left: ["address"], footer_center: ["none"], footer_right: ["signatures"],
          header_align: "center", footer_align: "center",
        };
        break;
      default: break;
    }
    setMany(patch);
    toast.success("Preset applied — Save letterhead to persist");
  };

  return (
    <Card className="mt-6 p-6 rounded-2xl" data-testid="advanced-letterhead-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Frame className="w-5 h-5 text-efgh-blue" /> Advanced layout & finishing</div>
          <p className="text-sm text-ink/60">Fine-tune page margins, spacing, borders, watermark and placement. Everything updates the live preview instantly.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-advanced-letterhead" className="bg-ink hover:bg-ink/90 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save layout
        </Button>
      </div>

      {/* ================= Phase E — Layout presets ================= */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><Layout className="w-3.5 h-3.5" /> Print page size</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "A4",     label: "A4",     hint: "210 × 297 mm" },
            { id: "Letter", label: "Letter", hint: "8.5 × 11 in" },
            { id: "Legal",  label: "Legal",  hint: "8.5 × 14 in" },
          ].map((p) => (
            <button key={p.id} type="button" onClick={() => set("page_size", p.id)} data-testid={`page-size-${p.id.toLowerCase()}`}
              className={`h-14 rounded-md border text-xs flex flex-col items-center justify-center transition-colors ${(lh.page_size || "A4") === p.id ? "bg-ink text-white border-ink shadow-sm" : "border-border text-ink/70 hover:bg-muted"}`}>
              <span className="font-semibold">{p.label}</span>
              <span className={`text-[10px] ${(lh.page_size || "A4") === p.id ? "text-white/70" : "text-ink/40"}`}>{p.hint}</span>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-ink/40 mt-1.5">The report body auto-adapts to this page size — content flows across pages leaving room for the running header/footer heights configured in the Letterhead tab.</p>
      </div>

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><Layout className="w-3.5 h-3.5" /> Layout preset</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            { id: "classic", label: "Classic" },
            { id: "logo-left-stacked", label: "Logo-left stacked" },
            { id: "centered-hero", label: "Centered hero" },
            { id: "split-modern", label: "Split modern" },
            { id: "minimal", label: "Minimal" },
          ].map((p) => (
            <button key={p.id} type="button" onClick={() => applyPreset(p.id)} data-testid={`preset-${p.id}`} className="h-10 rounded-md border border-border text-xs text-ink/80 hover:bg-muted hover:border-ink/40 transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-ink/40 mt-1.5">One-click populates header/footer slots + alignment. You can still tweak individual slots above afterwards.</p>
      </div>

      {/* ================= Phase E — Alignment ================= */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <AlignmentPicker label="Header alignment" value={lh.header_align} onChange={(v) => set("header_align", v)} testid="header-align" />
        <AlignmentPicker label="Footer alignment" value={lh.footer_align} onChange={(v) => set("footer_align", v)} testid="footer-align" />
      </div>

      {/* ================= Phase B — Page margins ================= */}
      <div className="mt-6 pt-6 border-t border-border/60">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><Ruler className="w-3.5 h-3.5" /> Page margins (millimetres)</div>
        <p className="text-[11px] text-ink/50 mb-3">Extra whitespace around the printed A4 page. Header & footer are pushed in by top/bottom margins.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MmInput label="Top" value={lh.page_margin_top_mm} onChange={(v) => set("page_margin_top_mm", v)} testid="pm-top" max={40} />
          <MmInput label="Right" value={lh.page_margin_right_mm} onChange={(v) => set("page_margin_right_mm", v)} testid="pm-right" max={40} />
          <MmInput label="Bottom" value={lh.page_margin_bottom_mm} onChange={(v) => set("page_margin_bottom_mm", v)} testid="pm-bottom" max={40} />
          <MmInput label="Left" value={lh.page_margin_left_mm} onChange={(v) => set("page_margin_left_mm", v)} testid="pm-left" max={40} />
        </div>
      </div>

      {/* ================= Phase D — Element gaps ================= */}
      <div className="mt-6 pt-6 border-t border-border/60">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><SquareStack className="w-3.5 h-3.5" /> Element gaps</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Gap between report sections (mm)</Label>
            <Input type="number" min={0} max={40} value={lh.section_gap_mm ?? 5} onChange={(e) => set("section_gap_mm", parseInt(e.target.value, 10) || 0)} data-testid="section-gap" />
            <p className="text-[10px] text-ink/40 mt-1">Vertical breathing room between Summary / Biomarkers / Lifestyle blocks in the printed PDF.</p>
          </div>
          <div>
            <Label>Gap between stacked slot chips (px)</Label>
            <Input type="number" min={0} max={40} value={lh.slot_gap_px ?? 4} onChange={(e) => set("slot_gap_px", parseInt(e.target.value, 10) || 0)} data-testid="slot-gap" />
            <p className="text-[10px] text-ink/40 mt-1">Space between multiple fields stacked in the same header/footer column (e.g. phone then email then WhatsApp).</p>
          </div>
        </div>
      </div>

      {/* ================= Phase C — Border style + Watermark ================= */}
      <div className="mt-6 pt-6 border-t border-border/60">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><Frame className="w-3.5 h-3.5" /> Border style</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {["solid", "dashed", "dotted", "double", "groove"].map((s) => (
            <button key={s} type="button" onClick={() => set("border_style", s)} data-testid={`border-style-${s}`}
              className={`h-11 rounded-md border-2 flex items-center justify-center text-xs capitalize transition-colors ${lh.border_style === s ? "bg-ink text-white border-ink" : "border-border text-ink/70 hover:bg-muted"}`}>
              <span className="inline-block w-16 mr-2" style={{ borderBottom: `3px ${s} ${lh.border_style === s ? "#ffffff" : (lh.border_color || "#0B7A4D")}` }} />
              {s}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-ink/40 mt-1.5">Style applies to all four border zones below. Thickness/colour is set per-zone.</p>
      </div>

      {/* ================= Phase F — Border placements (4 zones) ================= */}
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><Frame className="w-3.5 h-3.5" /> Border placement (px thickness · 0 = off)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <BorderZoneCard label="Above header content" hint="Top edge of the letterhead" value={lh.border_above_header_thickness} onChange={(v) => set("border_above_header_thickness", v)} color={lh.border_color} style={lh.border_style} testid="border-above-header" />
          <BorderZoneCard label="Below header content" hint="Divider between header and body" value={lh.border_below_header_thickness} onChange={(v) => set("border_below_header_thickness", v)} color={lh.border_color} style={lh.border_style} testid="border-below-header" />
          <BorderZoneCard label="Above footer content" hint="Divider between body and footer" value={lh.border_above_footer_thickness} onChange={(v) => set("border_above_footer_thickness", v)} color={lh.border_color} style={lh.border_style} testid="border-above-footer" />
          <BorderZoneCard label="Below footer content" hint="Bottom edge of the letterhead" value={lh.border_below_footer_thickness} onChange={(v) => set("border_below_footer_thickness", v)} color={lh.border_color} style={lh.border_style} testid="border-below-footer" />
        </div>
      </div>

      {/* ================= Phase F — Header/Footer inner padding ================= */}
      <div className="mt-6 pt-6 border-t border-border/60">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><SquareStack className="w-3.5 h-3.5" /> Content offset from header/footer edges (mm)</div>
        <p className="text-[11px] text-ink/50 mb-3">Space between the border and the actual slot contents. Increase to push elements away from the top/bottom edge of the header/footer.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 rounded-xl border border-border">
            <div className="text-[11px] font-semibold text-ink/70 mb-2">Header padding</div>
            <div className="grid grid-cols-2 gap-2">
              <MmInput label="From top" value={lh.header_padding_top_mm} onChange={(v) => set("header_padding_top_mm", v)} testid="hp-top" max={30} />
              <MmInput label="From bottom" value={lh.header_padding_bottom_mm} onChange={(v) => set("header_padding_bottom_mm", v)} testid="hp-bottom" max={30} />
            </div>
          </div>
          <div className="p-3 rounded-xl border border-border">
            <div className="text-[11px] font-semibold text-ink/70 mb-2">Footer padding</div>
            <div className="grid grid-cols-2 gap-2">
              <MmInput label="From top" value={lh.footer_padding_top_mm} onChange={(v) => set("footer_padding_top_mm", v)} testid="fp-top" max={30} />
              <MmInput label="From bottom" value={lh.footer_padding_bottom_mm} onChange={(v) => set("footer_padding_bottom_mm", v)} testid="fp-bottom" max={30} />
            </div>
          </div>
        </div>
      </div>

      {/* ================= Phase F — Logo position override ================= */}
      <div className="mt-6 pt-6 border-t border-border/60">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><Move className="w-3.5 h-3.5" /> Fine-tune logo position (mm)</div>
        <p className="text-[11px] text-ink/50 mb-3">Nudge the logo left/right and up/down independently of its slot column. Positive values move the logo right/down; negative move left/up.</p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
          <div>
            <Label>Horizontal offset (X)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="range" min={-40} max={40} step={0.5} value={lh.logo_offset_x_mm ?? 0} onChange={(e) => set("logo_offset_x_mm", parseFloat(e.target.value))} className="flex-1" data-testid="logo-offset-x-slider" />
              <Input type="number" min={-40} max={40} step={0.5} value={lh.logo_offset_x_mm ?? 0} onChange={(e) => set("logo_offset_x_mm", parseFloat(e.target.value) || 0)} className="w-20 h-9 tabular-nums" data-testid="logo-offset-x" />
              <span className="text-[10px] text-ink/40">mm</span>
            </div>
          </div>
          <div>
            <Label>Vertical offset (Y)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="range" min={-30} max={30} step={0.5} value={lh.logo_offset_y_mm ?? 0} onChange={(e) => set("logo_offset_y_mm", parseFloat(e.target.value))} className="flex-1" data-testid="logo-offset-y-slider" />
              <Input type="number" min={-30} max={30} step={0.5} value={lh.logo_offset_y_mm ?? 0} onChange={(e) => set("logo_offset_y_mm", parseFloat(e.target.value) || 0)} className="w-20 h-9 tabular-nums" data-testid="logo-offset-y" />
              <span className="text-[10px] text-ink/40">mm</span>
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => setMany({ logo_offset_x_mm: 0, logo_offset_y_mm: 0 })} data-testid="logo-offset-reset" className="h-9 mt-6 text-xs">Reset</Button>
        </div>
        <p className="text-[10px] text-ink/40 mt-2">💡 To fully re-place the logo (e.g. left → center → right column), also change which header/footer slot contains &quot;Logo&quot; from the Letterhead tab above.</p>
      </div>

      <div className="mt-6 pt-6 border-t border-border/60">
        <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2 flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" /> Watermark</div>
        <div className="grid md:grid-cols-[1fr_180px] gap-3 items-start">
          <div>
            <Label>Watermark image URL (optional)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={lh.watermark_url || ""} onChange={(e) => set("watermark_url", e.target.value)} placeholder="Paste image URL or upload →" data-testid="watermark-url" />
              <input ref={wmInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadWatermark(f); }} data-testid="watermark-file" />
              <Button type="button" variant="outline" onClick={() => wmInputRef.current?.click()} disabled={uploading} data-testid="watermark-upload">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload
              </Button>
              {lh.watermark_url && (
                <Button type="button" variant="outline" onClick={() => set("watermark_url", "")} data-testid="watermark-clear" title="Remove watermark">
                  <Trash2 className="w-4 h-4 text-danger" />
                </Button>
              )}
            </div>
            <p className="text-[10px] text-ink/40 mt-1">PNG with transparent bg works best. Rendered subtly behind the header area.</p>
          </div>
          <div>
            <Label>Opacity</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="range" min={0} max={0.5} step={0.02} value={lh.watermark_opacity ?? 0.06} onChange={(e) => set("watermark_opacity", parseFloat(e.target.value))} className="flex-1" data-testid="watermark-opacity" />
              <span className="text-xs text-ink/60 tabular-nums w-10 text-right">{Math.round((lh.watermark_opacity ?? 0.06) * 100)}%</span>
            </div>
          </div>
        </div>
        {lh.watermark_url && (
          <div className="mt-3 p-3 rounded-md border border-border bg-muted/30 flex items-center gap-3">
            <img src={lh.watermark_url} alt="watermark" className="h-12 w-12 object-contain" style={{ opacity: lh.watermark_opacity ?? 0.06 }} />
            <div className="text-[11px] text-ink/60">Live opacity preview — watermark will render at this transparency behind your letterhead.</div>
          </div>
        )}
      </div>
    </Card>
  );
}

function MmInput({ label, value, onChange, testid, max = 40 }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <Input type="number" min={0} max={max} value={value ?? 0} onChange={(e) => onChange(Math.max(0, Math.min(max, parseInt(e.target.value, 10) || 0)))} data-testid={testid} />
        <span className="text-[10px] text-ink/40">mm</span>
      </div>
    </div>
  );
}

function AlignmentPicker({ label, value, onChange, testid }) {
  const OPTIONS = [
    { id: "start", label: "Top", glyph: "▲" },
    { id: "center", label: "Center", glyph: "●" },
    { id: "end", label: "Bottom", glyph: "▼" },
  ];
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 grid grid-cols-3 gap-2">
        {OPTIONS.map((o) => (
          <button key={o.id} type="button" onClick={() => onChange(o.id)} data-testid={`${testid}-${o.id}`}
            className={`h-10 rounded-md border text-xs transition-colors ${value === o.id ? "bg-ink text-white border-ink" : "border-border text-ink/70 hover:bg-muted"}`}>
            <span className="mr-1">{o.glyph}</span>{o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function BorderZoneCard({ label, hint, value, onChange, color, style, testid }) {
  const v = value ?? 0;
  return (
    <div className={`p-3 rounded-xl border ${v ? "border-ink/40 bg-ink/[0.02]" : "border-border"} transition-colors`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink">{label}</div>
          <div className="text-[10px] text-ink/50">{hint}</div>
        </div>
        {v > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-ink/10 text-ink/70 font-medium whitespace-nowrap">{v}px</span>}
      </div>
      <div className="flex items-center gap-2">
        <input type="range" min={0} max={8} step={1} value={v} onChange={(e) => onChange(parseInt(e.target.value, 10))} className="flex-1" data-testid={`${testid}-slider`} />
        <Input type="number" min={0} max={12} value={v} onChange={(e) => onChange(Math.max(0, Math.min(12, parseInt(e.target.value, 10) || 0)))} className="w-16 h-8 text-xs tabular-nums" data-testid={`${testid}-input`} />
      </div>
      <div className="mt-2 h-3 rounded-sm bg-muted/40 flex items-center justify-center" title="Preview">
        {v > 0 ? <div className="w-full" style={{ borderBottom: `${v}px ${style || "solid"} ${color || "#0B7A4D"}` }} /> : <div className="text-[9px] text-ink/30 italic">off</div>}
      </div>
    </div>
  );
}
